import { matchPath } from 'react-router-dom';

import type { Crumb } from './pageChrome';

/**
 * Where a route sits in the app: its title, and the route above it.
 *
 * `parent` is a concrete href, not a pattern — it is both the lookup key for the
 * ancestor's title and the link the breadcrumb points at.
 */
export interface RouteMeta {
    title: string;
    parent?: string;
    /**
     * Rendered outside AppLayout (auth, the welcome flow), so there is no top bar
     * and no breadcrumb trail — the entry exists so the browser tab still gets a
     * title. Never give one a `parent`: it has nowhere to draw the trail.
     */
    chromeless?: true;
}

/**
 * The page hierarchy, declared once.
 *
 * `<Page>` reads its title and its whole breadcrumb trail from here, so a page
 * states neither. Adding a route means adding an entry — that is the only step,
 * and skipping it fails loudly in dev rather than silently rendering an
 * untitled page.
 *
 * Keys are route patterns (`matchPath` syntax). Literal keys win over patterns,
 * which is what lets `/settings` be the parent of `/settings/:section?` without
 * the two resolving to each other.
 *
 * Redirect-only routes (`<Navigate>`) need no entry unless another route uses
 * them as a breadcrumb parent (for example `/analytics` and `/config-studio`).
 */
export const ROUTE_META: Record<string, RouteMeta> = {
    '/': { title: 'Home' },
    '/config-studio/health': { title: 'Health', parent: '/config-studio' },
    '/config-studio/rose-for-agents': { title: 'Rose for Agents', parent: '/config-studio' },
    '/config-studio/rose-for-agents/access': {
        title: 'Agent access',
        parent: '/config-studio/rose-for-agents',
    },
    '/config-studio/rose-for-agents/checks': {
        title: 'Surface checks',
        parent: '/config-studio/rose-for-agents',
    },
    '/config-studio/rose-for-agents/profile': {
        title: 'What AI agents see',
        parent: '/config-studio/rose-for-agents',
    },
    '/accounts': { title: 'Accounts' },
    '/visitors': { title: 'Visitors' },
    '/conversations': { title: 'Conversations' },

    // Analytics
    '/analytics': { title: 'Analytics' },
    '/analytics/:tab': { title: 'Analytics', parent: '/analytics' },
    '/analytics/topics': { title: 'Topics', parent: '/analytics' },
    '/analytics/insights': { title: 'Insights', parent: '/analytics' },

    // Config Studio
    '/config-studio': { title: 'Config Studio' },
    '/config-studio/website-agent/playground': {
        title: 'Playground',
        parent: '/config-studio/website-agent',
    },
    '/config-studio/website-agent/evaluations': {
        title: 'Evaluations',
        parent: '/config-studio/website-agent',
    },
    '/config-studio/integration': { title: 'Integrations', parent: '/config-studio' },
    '/config-studio/:agentId': { title: 'Agent', parent: '/config-studio' },

    // Config Studio › Data Sources (tabbed screen: Website / Documents / FAQ / Lookup)
    '/config-studio/data-sources': { title: 'Data Sources', parent: '/config-studio' },
    '/config-studio/data-sources/:tab': { title: 'Data Sources', parent: '/config-studio' },

    // Config Studio › Sales Guidelines (tabbed screen: Pricing / Competitors)
    '/config-studio/sales-guidelines': {
        title: 'Sales Guidelines',
        parent: '/config-studio',
    },
    '/config-studio/sales-guidelines/:tab': {
        title: 'Sales Guidelines',
        parent: '/config-studio',
    },

    // Config Studio › Website Agent
    '/config-studio/website-agent': { title: 'Website Agent', parent: '/config-studio' },
    '/config-studio/website-agent/publish': {
        title: 'Publish',
        parent: '/config-studio/website-agent',
    },
    '/config-studio/website-agent/ai-sections': {
        title: 'AI Sections',
        parent: '/config-studio/website-agent',
    },
    '/config-studio/website-agent/curated-content': {
        title: 'Curated Content',
        parent: '/config-studio/website-agent',
    },
    '/config-studio/website-agent/ab-test': {
        title: 'A/B test',
        parent: '/config-studio/website-agent',
    },
    '/config-studio/brand': { title: 'Brand & tone', parent: '/config-studio' },
    '/config-studio/website-agent/engagement': {
        title: 'Starter questions',
        parent: '/config-studio/website-agent',
    },
    '/config-studio/website-agent/ctas': {
        title: 'Calls-to-action',
        parent: '/config-studio/website-agent',
    },
    // Schema-driven settings screens: the title is the schema's, passed explicitly.
    '/config-studio/website-agent/:slug': {
        title: 'Setting',
        parent: '/config-studio/website-agent',
    },

    // Config Studio › Content Agent
    '/config-studio/content-agent': { title: 'GEO Agent', parent: '/config-studio' },
    '/config-studio/content-agent/observatory': {
        title: 'Visitor Questions',
        parent: '/config-studio/content-agent',
    },
    '/config-studio/content-agent/setup': {
        title: 'GEO Agent settings',
        parent: '/config-studio/content-agent',
    },
    '/config-studio/content-agent/domain': {
        title: 'Custom domain',
        parent: '/config-studio/content-agent/setup',
    },
    '/config-studio/content-agent/settings': {
        title: 'Pipeline Settings',
        parent: '/config-studio/content-agent/setup',
    },

    // Standalone
    '/dynamic-questions': { title: 'Dynamic Questions' },
    '/documentation': { title: 'Documentation' },
    '/support': { title: 'Support' },
    '/design-system': { title: 'Design System' },
    '/settings': { title: 'Settings' },
    '/settings/plan-and-usage': { title: 'Plan & usage', parent: '/settings' },
    '/settings/compliance': { title: 'Compliance', parent: '/settings' },
    '/settings/compliance/:tab': { title: 'Compliance', parent: '/settings' },
    '/settings/:section?': { title: 'Team', parent: '/settings' },

    // Super Admin — host-staff-only. `/superadmin` is a redirect-only landing
    // that exists here so every page below can draw it as a breadcrumb parent.
    // Titles match the Sidebar labels so a crumb never disagrees with the link.
    '/superadmin': { title: 'Super Admin' },
    '/superadmin/clients/stats': { title: 'Client stats', parent: '/superadmin' },
    '/superadmin/clients/edit': { title: 'Edit clients', parent: '/superadmin' },
    '/superadmin/clients/edit/:clientId': {
        title: 'Edit client',
        parent: '/superadmin/clients/edit',
    },
    '/superadmin/users': { title: 'Users', parent: '/superadmin' },
    '/superadmin/sites': { title: 'Workspaces', parent: '/superadmin' },
    '/superadmin/experiments': { title: 'Experiments', parent: '/superadmin' },
    '/superadmin/experiments/new': { title: 'New test', parent: '/superadmin/experiments' },
    '/superadmin/experiments/:experimentId': { title: 'Test', parent: '/superadmin/experiments' },
    '/superadmin/annotations': { title: 'Annotations', parent: '/superadmin' },
    '/superadmin/person-identity': { title: 'Identity', parent: '/superadmin' },
    '/superadmin/notices': { title: 'Notices', parent: '/superadmin' },
    '/superadmin/webmcp': { title: 'AI agent usage', parent: '/superadmin' },

    // Outside AppLayout — tab title only, no breadcrumb.
    '/auth/callback': { title: 'Signing you in', chromeless: true },
    '/oauth/consent': { title: 'Connect your assistant', chromeless: true },
    '/agent-claim': { title: 'Connect your assistant', chromeless: true },
    '/superadmin/dq-lab': { title: 'Dynamic questions lab', chromeless: true },
    '/welcome/loader': { title: 'Setting up your agent', chromeless: true },
    '/welcome/plans': { title: 'Choose your plan', chromeless: true },
};

/** Literal key first, then the most specific matching pattern. */
export function lookupRouteMeta(pathname: string): RouteMeta | undefined {
    const exact = ROUTE_META[pathname];
    if (exact) return exact;

    const patterns = Object.keys(ROUTE_META)
        .filter((p) => p.includes(':'))
        // Longest pattern first: /superadmin/experiments/new must not lose to a
        // shallower pattern that also matches.
        .sort((a, b) => b.length - a.length);
    const hit = patterns.find((p) => matchPath(p, pathname));
    return hit ? ROUTE_META[hit] : undefined;
}

/** Guards against a mistyped `parent` looping back into its own subtree. */
const MAX_DEPTH = 10;

/**
 * The full chrome for a route: its title plus every ancestor, outermost first.
 * The title itself is NOT included — AppLayout appends it as the trail's leaf.
 */
export function resolveRouteChrome(
    pathname: string
): { title: string; breadcrumbs: Crumb[] } | undefined {
    const meta = lookupRouteMeta(pathname);
    if (!meta) return undefined;

    const breadcrumbs: Crumb[] = [];
    const seen = new Set<string>([pathname]);
    let parent = meta.parent;
    while (parent && !seen.has(parent) && breadcrumbs.length < MAX_DEPTH) {
        seen.add(parent);
        const parentMeta = lookupRouteMeta(parent);
        if (!parentMeta) break;
        breadcrumbs.unshift({ label: parentMeta.title, href: parent });
        parent = parentMeta.parent;
    }

    return { title: meta.title, breadcrumbs };
}
